import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Spinner,
  Form,
  InputGroup,
  Pagination,
  Modal,
  Nav,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaPlay,
  FaStop,
  FaChartBar,
  FaBook,
  FaQuestionCircle,
  FaUserPlus,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import TestService from "../../../services/testService";
import QuestionCategories from "./QuestionCategories";
import ManageQuestions from "./ManageQuestions";
import AssignToStudents from "../../../components/admin/tests/AssignToStudents";

const TestManagement = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [activeTab, setActiveTab] = useState("tests");
  const [rawResponse, setRawResponse] = useState(null);

  // For Categories & Questions
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState("");

  // For Assign to Students
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTestForAssign, setSelectedTestForAssign] = useState(null);

  // Pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1,
  });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (activeTab === "tests") {
      loadTests();
    }
  }, [pagination.current_page, itemsPerPage, searchTerm, activeTab]);

  const loadTests = async () => {
    try {
      setLoading(true);
      const response = await TestService.getTests(
        pagination.current_page,
        itemsPerPage,
        searchTerm,
      );
      setRawResponse(response);
      console.log("Tests response:", response);

      let testsArray = [];
      let paginationData = null;

      let parsedResponse = response;
      if (typeof response === 'string') {
        try {
          parsedResponse = JSON.parse(response);
        } catch (e) {
          console.error("Failed to parse response:", e);
        }
      }

      if (parsedResponse) {
        if (Array.isArray(parsedResponse)) {
          testsArray = parsedResponse;
        } else if (parsedResponse.data) {
          if (Array.isArray(parsedResponse.data)) {
            testsArray = parsedResponse.data;
          } else if (parsedResponse.data.data && Array.isArray(parsedResponse.data.data)) {
            testsArray = parsedResponse.data.data;
            paginationData = parsedResponse.data.pagination;
          } else if (parsedResponse.data.tests && Array.isArray(parsedResponse.data.tests)) {
            testsArray = parsedResponse.data.tests;
          }
        }
      }

      console.log("Extracted testsArray:", testsArray);

      setTests(testsArray || []);
      if (paginationData) {
        setPagination(paginationData);
      }
    } catch (error) {
      console.error("Failed to load tests:", error);
      toast.error("Failed to load tests");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTest) return;
    try {
      await TestService.deleteTest(selectedTest.id);
      toast.success("Test deleted successfully");
      setShowDeleteModal(false);
      setSelectedTest(null);
      loadTests();
    } catch (error) {
      toast.error("Failed to delete test");
    }
  };

  const handleStatusChange = async (test, newStatus) => {
    try {
      await TestService.updateTestStatus(test.id, newStatus);
      toast.success(
        `Test ${newStatus === "published" ? "published" : "closed"} successfully`,
      );
      loadTests();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleViewResults = (test) => {
    navigate(`/admin/tests/results/${test.id}`);
  };

  const handleSelectCategory = (categoryId) => {
    setSelectedCategoryId(categoryId);
    TestService.getCategories()
      .then((res) => {
        const category = res.data?.find((c) => c.id === categoryId);
        if (category) {
          setSelectedCategoryName(category.name);
        }
      })
      .catch((err) => console.error(err));
  };

  const handleAssignSuccess = () => {
    loadTests();
    toast.success("Assignment updated successfully");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "published":
        return <Badge bg="success">Published</Badge>;
      case "draft":
        return <Badge bg="warning">Draft</Badge>;
      case "closed":
        return <Badge bg="secondary">Closed</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.total_pages) {
      setPagination((prev) => ({ ...prev, current_page: pageNumber }));
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const renderPagination = () => {
    const { current_page, total_pages } = pagination;
    if (total_pages <= 1) return null;

    let items = [];
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(current_page - 1)}
        disabled={current_page === 1}
      />,
    );

    for (let i = 1; i <= total_pages; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === current_page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(current_page + 1)}
        disabled={current_page === total_pages}
      />,
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <Form.Select
            size="sm"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={{ width: "auto" }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div className="text-muted small">
          Showing {(pagination.current_page - 1) * itemsPerPage + 1} -{" "}
          {Math.min(pagination.current_page * itemsPerPage, pagination.total)}{" "}
          of {pagination.total} tests
        </div>
        <Pagination size="sm">{items}</Pagination>
      </div>
    );
  };

  return (
    <div>
      <Header />
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3>
            <FaChartBar className="me-2 text-primary" />
            Test Management
          </h3>
          <Button
            variant="primary"
            onClick={() => navigate("/admin/tests/create")}
          >
            <FaPlus className="me-1" /> Create New Test
          </Button>
        </div>

        {/* Tabs */}
        <Card className="shadow-sm">
          <Card.Header>
            <Nav
              variant="tabs"
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
            >
              <Nav.Item>
                <Nav.Link eventKey="tests">
                  <FaChartBar className="me-2" /> Tests
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="categories">
                  <FaBook className="me-2" /> Question Categories
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="questions">
                  <FaQuestionCircle className="me-2" /> Question Bank
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>
          <Card.Body>
            {/* Tests Tab */}
            {activeTab === "tests" && (
              <>
                {/* Search Bar */}
                <div className="mb-3">
                  <InputGroup style={{ width: "300px" }}>
                    <Form.Control
                      placeholder="Search tests by title..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    />
                    <Button variant="outline-primary" onClick={handleSearch}>
                      <FaSearch />
                    </Button>
                  </InputGroup>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : tests.length === 0 ? (
                  <Alert variant="info" className="text-center">
                    No tests created yet. Click "Create New Test" to get
                    started.
                  </Alert>
                ) : (
                  <>
                    <Table striped hover responsive className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>Title</th>
                          <th>Category</th>
                          <th>Questions</th>
                          <th>Duration</th>
                          <th>Passing %</th>
                          <th>Assigned To</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tests.map((test) => (
                          <tr key={test.id}>
                            <td>
                              <strong>{test.title}</strong>
                              {test.description && (
                                <div className="small text-muted">
                                  {test.description.substring(0, 50)}...
                                </div>
                              )}
                            </td>
                            <td>{test.category_name}</td>
                            <td>
                              {test.total_questions} /{" "}
                              {test.available_questions}
                            </td>
                            <td>{test.duration_minutes} min</td>
                            <td>{test.passing_percentage}%</td>
                            <td>
                              {test.course_name && (
                                <Badge bg="info">
                                  Course: {test.course_name}
                                </Badge>
                              )}
                              {test.batch_name && (
                                <Badge bg="success">
                                  Batch: {test.batch_name}
                                </Badge>
                              )}
                              {!test.course_name && !test.batch_name && (
                                <span className="text-muted">Not assigned</span>
                              )}
                            </td>
                            <td>{getStatusBadge(test.status)}</td>
                            <td>{formatDate(test.created_at)}</td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-1"
                                onClick={() =>
                                  navigate(`/admin/tests/edit/${test.id}`)
                                }
                                title="Edit Test"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-success"
                                size="sm"
                                className="me-1"
                                onClick={() => {
                                  setSelectedTestForAssign(test);
                                  setShowAssignModal(true);
                                }}
                                title="Assign to Students"
                              >
                                <FaUserPlus />
                              </Button>
                              <Button
                                variant="outline-info"
                                size="sm"
                                className="me-1"
                                onClick={() => handleViewResults(test)}
                                title="View Results"
                              >
                                <FaChartBar />
                              </Button>
                              {test.status === "draft" && (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  className="me-1"
                                  onClick={() =>
                                    handleStatusChange(test, "published")
                                  }
                                  title="Publish"
                                >
                                  <FaPlay />
                                </Button>
                              )}
                              {test.status === "published" && (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="me-1"
                                  onClick={() =>
                                    handleStatusChange(test, "closed")
                                  }
                                  title="Close"
                                >
                                  <FaStop />
                                </Button>
                              )}
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  setSelectedTest(test);
                                  setShowDeleteModal(true);
                                }}
                                title="Delete"
                              >
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {renderPagination()}
                  </>
                )}
              </>
            )}

            {/* Categories Tab */}
            {activeTab === "categories" && (
              <QuestionCategories
                onSelectCategory={handleSelectCategory}
                selectedCategoryId={selectedCategoryId}
              />
            )}

            {/* Questions Tab */}
            {activeTab === "questions" && (
              <ManageQuestions
                categoryId={selectedCategoryId}
                categoryName={selectedCategoryName}
              />
            )}
          </Card.Body>
        </Card>

        {/* Delete Modal */}
        <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title className="text-danger">Delete Test</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedTest && (
              <>
                <p>Are you sure you want to delete the test:</p>
                <p className="fw-bold text-danger">{selectedTest.title}</p>
                <Alert variant="warning">
                  This will also delete all student attempts for this test.
                </Alert>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Assign to Students Modal */}
        {selectedTestForAssign && (
          <AssignToStudents
            show={showAssignModal}
            onHide={() => {
              setShowAssignModal(false);
              setSelectedTestForAssign(null);
            }}
            testId={selectedTestForAssign.id}
            testTitle={selectedTestForAssign.title}
            onSuccess={handleAssignSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default TestManagement;